/**
 * Tiny adapter so the React app can lazily use Capacitor APIs (Share, Browser,
 * Geolocation, etc.) when running inside the native shell — and fall back to
 * web APIs when running in a browser.
 *
 * Why this file exists:
 *   Capacitor JS plugins are tree-shaken safe and harmless to import in a web
 *   build. But to keep our existing web code untouched we centralise the
 *   "am I on a native device?" check + the few cross-platform helpers here.
 */
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { Share } from "@capacitor/share";
import { Geolocation } from "@capacitor/geolocation";
import { StatusBar, Style } from "@capacitor/status-bar";
import { SplashScreen } from "@capacitor/splash-screen";

export const isNative = () => Capacitor.isNativePlatform();
export const platform = () => Capacitor.getPlatform();

/**
 * Open an external URL. On native, uses the in-app Safari View Controller / Chrome
 * Custom Tabs so the user never leaves the app. On web, falls back to window.open.
 * Used by the Stripe Checkout redirect flow.
 */
export async function openExternal(url) {
  if (isNative()) {
    await Browser.open({ url, presentationStyle: "popover" });
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

/** Cross-platform share — uses the native share sheet on iOS/Android. */
export async function shareLink(title, url, text) {
  if (isNative()) {
    try {
      await Share.share({ title, url, text: text || title });
      return true;
    } catch {
      return false; // user cancelled or share unavailable
    }
  }
  if (typeof navigator !== "undefined" && "share" in navigator) {
    try {
      await navigator.share({ title, url, text });
      return true;
    } catch {
      return false;
    }
  }
  if (typeof navigator !== "undefined" && navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Cross-platform geolocation. On native we use the Capacitor plugin (which uses
 * the native LocationManager / CLLocationManager and triggers the proper
 * system permission prompt). On web we fall back to navigator.geolocation.
 */
export async function currentPosition() {
  if (isNative()) {
    const r = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });
    return { lat: r.coords.latitude, lng: r.coords.longitude };
  }
  return new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new Error("Geolocation unavailable"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      reject,
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });
}

/**
 * Run once on app boot in native mode to set the status-bar tint and hide the
 * splash once React has mounted. Called from src/index.js.
 */
export async function initNativeShell() {
  if (!isNative()) return;
  try {
    await StatusBar.setStyle({ style: Style.Light });
    await StatusBar.setBackgroundColor({ color: "#1B3624" });
  } catch {
    /* StatusBar isn't available on web — safe to ignore */
  }
  try {
    await SplashScreen.hide({ fadeOutDuration: 250 });
  } catch {
    /* fine on web */
  }
}
